import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModificationsTimelineView } from './ModificationsTimelineView';
import { ModificationNestedCards } from './vehicle-detail/ModificationNestedCards';
import { ModificationNestedList } from './vehicle-detail/ModificationNestedList';
import type { HeroImage, VehicleDetailModification } from './vehicle-detail/types';
import { ExportBuildListModal } from './ExportBuildListModal';
import { MoveModificationModal } from './MoveModificationModal';
import { categories as categoryOptions } from '../utils/categories';
import {
  filterAndSortPublicBuild,
  parsePublicBuildQuery,
  uniqueBrands,
  uniqueCategories,
  type PublicBuildFilters,
  type PublicBuildMod,
  type PublicBuildView,
} from '../utils/publicBuildFilters';
import { expandModsWithAncestors } from '../utils/buildExplorerUtils';
import type { PassportCard } from '../services/api/passportCards';
import { useCarShowAvailable } from '../hooks/useCarShowAvailable';
import { setScanButtonHidden } from '../utils/nativeShell';
import type { ShowModeSnapshot } from '../utils/carShowOffline';

const BuildExplorer = lazy(() =>
  import('./vehicle-detail/BuildExplorer').then((m) => ({ default: m.BuildExplorer }))
);

export interface VehicleBuildViewsProps {
  /** Optional editorial-page navigation; absent on the default build page. */
  navigationRequest?: BuildViewNavigation;
  onViewChange?: (view: 'explore' | 'list') => void;
  modifications: PublicBuildMod[];
  showPricing?: boolean;
  compact?: boolean;
  /** Sync view/filters to URL query params */
  syncUrl?: boolean;
  /** The editorial page defaults to Story, so Explore needs an explicit URL. */
  keepExploreInUrl?: boolean;
  /**
   * Page kiosk / QR (`display=1`): auto-enter Car Show Mode on Explore.
   * Complements `?carshow=1` for older QR stickers that only set display.
   */
  autoCarShow?: boolean;
  /** @deprecated Unused — kept for call-site compatibility */
  defaultCollapseLarge?: boolean;
  className?: string;
  canEdit?: boolean;
  isOwner?: boolean;
  vehicleLabel?: string;
  vehicleImageUrl?: string;
  vehicleGallery?: HeroImage[];
  collectorCard?: PassportCard;
  /** Separate public projection; owner browsing can contain private fields. */
  carShowContent?: Omit<ShowModeSnapshot, 'modifications' | 'allModifications'> & { modifications: PublicBuildMod[] };
  onEditModification?: (modification: VehicleDetailModification) => void;
  onDeleteModification?: (id: string) => void;
  onAddChildModification?: (parent: VehicleDetailModification) => void;
  onOrganizeMove?: (args: {
    modificationId: number;
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => Promise<void>;
  onOrganizeReorder?: (args: {
    parentModificationId: number | null;
    orderedIds: number[];
  }) => Promise<void>;
  onOrganizeBulkMove?: (args: {
    modificationIds: number[];
    parentModificationId: number | null;
    category?: string;
    beforeModificationId?: number | null;
    afterModificationId?: number | null;
  }) => Promise<void>;
  onOrganizeBulkCategory?: (args: {
    modificationIds: number[];
    category: string;
    includeNested: boolean;
  }) => Promise<void>;
  onOrganizeBulkDelete?: (args: {
    modificationIds: number[];
    childPolicy: 'promote' | 'cascade';
  }) => Promise<void>;
  emptyActions?: React.ReactNode;
  /** Notify parent when select/organize mode changes. */
  onSelectModeChange?: (on: boolean) => void;
}

export interface BuildViewNavigation {
  id: number;
  view: 'explore' | 'list';
  focusId?: string;
}

const VIEW_ICONS: Record<PublicBuildView, React.ReactElement> = {
  explore: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 14.5V17h-2v-.5a6 6 0 01-4.9-4.9H5.5v-2h.6A6 6 0 0111 5.5V5h2v.5a6 6 0 014.9 4.9h.6v2h-.6A6 6 0 0113 16.5zM12 8a4 4 0 100 8 4 4 0 000-8z" />
    </svg>
  ),
  card: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM6 8v10h12V8H6z" />
      <path d="M9 10h6v2H9v-2zM9 13h4v1H9v-1z" />
    </svg>
  ),
  list: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M4 6h16v2H4V6zM4 11h16v2H4v-2zM4 16h16v2H4v-2z" />
    </svg>
  ),
  timeline: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  ),
};

/** Visible build display tabs. Cards and Timeline views are intentionally hidden for now. */
const BUILD_VIEWS: { id: PublicBuildView; label: string }[] = [
  { id: 'explore', label: 'Explore' },
  { id: 'list', label: 'List' },
];

const DEFAULT_BUILD_VIEW: PublicBuildView = 'explore';

function toDetailMod(m: PublicBuildMod): VehicleDetailModification {
  return {
    id: String(m.id ?? ''),
    title: m.title,
    description: m.description ?? '',
    category: m.category || 'Uncategorized',
    vendor: m.vendor ?? '',
    cost: m.cost ?? undefined,
    install_date: m.install_date ?? undefined,
    notes: m.notes ?? undefined,
    parent_modification_id: m.parent_modification_id ?? null,
    parentComponent: m.parentComponent ?? undefined,
    specifications: m.specifications as VehicleDetailModification['specifications'],
    sort_order: m.sort_order ?? undefined,
    image_url: m.image_url ?? null,
    image_id: m.image_id ?? null,
  };
}

/**
 * Unified build display for visitors and owners.
 * Edit / organize actions only when canEdit.
 */
const VehicleBuildViews: React.FC<VehicleBuildViewsProps> = ({
  modifications,
  showPricing = false,
  syncUrl = false,
  keepExploreInUrl = false,
  autoCarShow = false,
  className = '',
  canEdit = false,
  isOwner = false,
  vehicleLabel,
  vehicleImageUrl,
  vehicleGallery,
  collectorCard,
  carShowContent,
  onEditModification,
  onDeleteModification,
  onAddChildModification,
  onOrganizeMove,
  onOrganizeReorder,
  onOrganizeBulkMove,
  onOrganizeBulkCategory,
  onOrganizeBulkDelete,
  emptyActions,
  onSelectModeChange: onSelectModeChangeProp,
  navigationRequest,
  onViewChange,
}) => {
  const [searchParams] = useSearchParams();
  const initial = parsePublicBuildQuery(searchParams.toString());
  const hasExplicitView = new URLSearchParams(
    searchParams.toString().startsWith('?') ? searchParams.toString().slice(1) : searchParams.toString()
  ).has('view');
  const showModeSupported = useCarShowAvailable();
  const carShowAvailable = showModeSupported && (carShowContent?.collectorCard ?? collectorCard)?.status !== 'private';
  const requestedCarShow = initial.carShow || autoCarShow;
  const startCarShow = requestedCarShow && carShowAvailable;

  const [view, setView] = useState<PublicBuildView>(() => {
    if (navigationRequest) return navigationRequest.view;
    if (startCarShow) return initial.view === 'list' ? 'list' : 'explore';
    if (!hasExplicitView) return DEFAULT_BUILD_VIEW;
    // Hidden views fall back to Explore for old shared links.
    if (initial.view === 'card' || initial.view === 'timeline') return DEFAULT_BUILD_VIEW;
    return initial.view;
  });
  const [search, setSearch] = useState(initial.q);
  const [category, setCategory] = useState<string>(initial.category || 'All');
  const [brand, setBrand] = useState<string>(initial.brand || 'All');
  const [sort, setSort] = useState<PublicBuildFilters['sort']>(canEdit ? 'order' : 'category');
  const [showSpecs, setShowSpecs] = useState(true);
  const [topLevelOnly, setTopLevelOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exploreFocus, setExploreFocus] = useState<string | null>(initial.focus);
  const [carShowMode, setCarShowMode] = useState(startCarShow);
  const carShowFromQr = startCarShow;
  const activeCarShowMode = carShowMode && carShowAvailable;
  const lastCarShowRequest = useRef(requestedCarShow);

  useEffect(() => {
    if (!navigationRequest) return;
    setView(navigationRequest.view);
    setExploreFocus(navigationRequest.focusId ?? null);
    if (navigationRequest.focusId) {
      setSearch('');
      setCategory('All');
      setBrand('All');
    }
  }, [navigationRequest]);

  useEffect(() => {
    if (view === 'explore' || view === 'list') onViewChange?.(view);
  }, [view, onViewChange]);

  useEffect(() => {
    if (!carShowAvailable) {
      setCarShowMode(false);
      // Older installed shells hide Scan on kiosk URLs; restore normal chrome.
      if (requestedCarShow) setScanButtonHidden(false);
    }
    else if (requestedCarShow && !lastCarShowRequest.current) setCarShowMode(true);
    lastCarShowRequest.current = requestedCarShow;
  }, [requestedCarShow, carShowAvailable]);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exportOpen, setExportOpen] = useState(!!initial.openExport);
  const [exportMods, setExportMods] = useState<PublicBuildMod[] | null>(null);
  const [moveTargets, setMoveTargets] = useState<VehicleDetailModification[] | null>(null);
  const [bulkCategoryOpen, setBulkCategoryOpen] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkIncludeNested, setBulkIncludeNested] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const updateSelectMode = (on: boolean) => {
    setSelectMode(on);
    if (!on) setSelectedIds(new Set());
    onSelectModeChangeProp?.(on);
  };

  const goToListOrganize = (startSelect = false) => {
    setView('list');
    if (startSelect) {
      updateSelectMode(true);
    } else {
      updateSelectMode(false);
    }
  };

  const views = BUILD_VIEWS;

  const categories = useMemo(() => uniqueCategories(modifications), [modifications]);
  const brands = useMemo(() => uniqueBrands(modifications), [modifications]);

  const exploreView = view === 'explore';
  const filters: PublicBuildFilters = useMemo(
    () => ({
      search,
      categories: category === 'All' ? ['All'] : [category],
      brands: brand === 'All' ? ['All'] : [brand],
      sort: exploreView ? (canEdit ? 'order' : 'category') : sort,
    }),
    [search, category, brand, sort, exploreView, canEdit]
  );

  const filtered = useMemo(() => {
    let list = filterAndSortPublicBuild(modifications, filters);
    if (!exploreView && topLevelOnly) {
      list = list.filter((m) => m.parent_modification_id == null || m.parent_modification_id === '');
    }
    return list;
  }, [modifications, filters, topLevelOnly, exploreView]);

  const detailMods = useMemo(() => filtered.map(toDetailMod), [filtered]);
  const allDetailMods = useMemo(() => modifications.map(toDetailMod), [modifications]);
  const exploreMods = useMemo(() => {
    const expanded = expandModsWithAncestors(modifications, filtered);
    return expanded.map(toDetailMod);
  }, [modifications, filtered]);

  useEffect(() => {
    if (activeCarShowMode && view !== 'explore' && view !== 'list') {
      setView('explore');
    }
  }, [activeCarShowMode, view]);

  // Deep-link without remounting: write URL via history.replaceState so React
  // Router does not re-render the page tree on every tab change.
  useEffect(() => {
    if (!syncUrl || typeof window === 'undefined') return;
    const next = new URLSearchParams(window.location.search);
    ['view', 'category', 'brand', 'q', 'focus', 'carshow', 'car_show'].forEach((k) => next.delete(k));
    if (view !== DEFAULT_BUILD_VIEW || keepExploreInUrl) next.set('view', view);
    if (category !== 'All') next.set('category', category);
    if (brand !== 'All') next.set('brand', brand);
    if (search.trim()) next.set('q', search.trim());
    if (view === 'explore' && exploreFocus) next.set('focus', exploreFocus);
    if (activeCarShowMode) next.set('carshow', '1');
    const qs = next.toString();
    const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', nextUrl);
    }
  }, [syncUrl, keepExploreInUrl, view, category, brand, search, exploreFocus, activeCarShowMode]);

  useEffect(() => {
    if (initial.openExport && canEdit) {
      setExportOpen(true);
      setView('list');
    }
    // Only on mount / legacy compact link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMods = useMemo(
    () => allDetailMods.filter((m) => selectedIds.has(String(m.id))),
    [allDetailMods, selectedIds]
  );

  const nestedImpact = useMemo(() => {
    const selected = new Set(selectedMods.map((m) => String(m.id)));
    let nested = 0;
    let parentsWithKids = 0;
    selectedMods.forEach((m) => {
      const kids = allDetailMods.filter(
        (c) => c.parent_modification_id != null && String(c.parent_modification_id) === String(m.id)
      );
      if (kids.length === 0) return;
      parentsWithKids += 1;
      const walk = (id: string) => {
        allDetailMods.forEach((c) => {
          if (c.parent_modification_id != null && String(c.parent_modification_id) === id) {
            if (!selected.has(String(c.id))) nested += 1;
            walk(String(c.id));
          }
        });
      };
      walk(String(m.id));
    });
    return { nested, parentsWithKids };
  }, [selectedMods, allDetailMods]);

  const inputClass = 'input text-sm';

  const hasActiveFilters =
    category !== 'All' || brand !== 'All' || (!exploreView && topLevelOnly) || search.trim().length > 0;

  if (modifications.length === 0 && !activeCarShowMode) {
    return (
      <div className={`rounded-xl border border-dashed border-white/[0.06] bg-[#0a0a0a]/40 py-10 text-center ${className}`}>
        <p className="text-sm text-muted mb-4">No modifications listed yet.</p>
        {emptyActions}
      </div>
    );
  }

  return (
    <div className={`space-y-4 md:space-y-5 build-views-shell min-w-0 overflow-x-clip ${className}`}>
      {!activeCarShowMode ? (
      <div className="build-chrome min-w-0 overflow-x-clip overflow-y-visible">
        <div className="tab-list" role="tablist" aria-label="Build view">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={view === v.id}
              title={v.label}
              aria-label={v.label}
              onClick={() => {
                setView(v.id);
                if (v.id !== 'list') {
                  updateSelectMode(false);
                }
              }}
              className={view === v.id ? 'tab-active' : 'tab'}
            >
              <span className="opacity-80 scale-110 md:scale-100">{VIEW_ICONS[v.id]}</span>
              <span className="whitespace-nowrap">{v.label}</span>
            </button>
          ))}
        </div>

        <div className="space-y-2.5 md:space-y-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-3">
            <div className="relative flex-1 min-w-0">
              <label htmlFor="vehicle-build-search" className="sr-only">
                Search parts
              </label>
              <input
                id="vehicle-build-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parts, brands, or specs…"
                className={inputClass}
              />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                className={
                  filtersOpen || hasActiveFilters || (!exploreView && !showSpecs)
                    ? 'filter-chip-active'
                    : 'filter-chip-inactive'
                }
                aria-expanded={filtersOpen}
              >
                Filters
                {(hasActiveFilters || (!exploreView && !showSpecs)) && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-md bg-orange-500/25 text-[11px] tabular-nums">
                    {[
                      category !== 'All',
                      brand !== 'All',
                      !exploreView && topLevelOnly,
                      search.trim().length > 0,
                      !exploreView && !showSpecs,
                    ].filter(Boolean).length || '·'}
                  </span>
                )}
              </button>
              <span className="text-meta tabular-nums whitespace-nowrap px-1">
                {filtered.length}/{modifications.length}
              </span>
            </div>
          </div>

          {/* Desktop/tablet quick toggles — Specs stays one tap away while browsing */}
          {!exploreView && <div className="hidden md:flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSpecs((v) => !v)}
              className={showSpecs ? 'filter-chip-active' : 'filter-chip-inactive'}
              aria-pressed={showSpecs}
            >
              Specs
            </button>
            <button
              type="button"
              onClick={() => setTopLevelOnly((v) => !v)}
              className={topLevelOnly ? 'filter-chip-active' : 'filter-chip-inactive'}
              aria-pressed={topLevelOnly}
              title="Hide parts nested under another part"
            >
              Top-level only
            </button>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setCategory('All');
                  setBrand('All');
                  setTopLevelOnly(false);
                  setShowSpecs(true);
                  setSort(canEdit ? 'order' : 'category');
                }}
                className="text-xs text-gray-500 hover:text-gray-300 min-h-[40px] px-2 cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>}

          {(category !== 'All' || brand !== 'All') && (
            <div className="flex flex-wrap gap-2">
              {category !== 'All' && (
                <button
                  type="button"
                  onClick={() => setCategory('All')}
                  className="filter-chip-active"
                >
                  {category}
                  <span aria-hidden className="opacity-70">
                    ×
                  </span>
                </button>
              )}
              {brand !== 'All' && (
                <button
                  type="button"
                  onClick={() => setBrand('All')}
                  className="filter-chip-active"
                >
                  {brand}
                  <span aria-hidden className="opacity-70">
                    ×
                  </span>
                </button>
              )}
            </div>
          )}

          {filtersOpen && (
            <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a]/80 p-3.5 space-y-3">
              {!exploreView && <div className="flex flex-wrap gap-2 md:hidden">
                <button
                  type="button"
                  onClick={() => setShowSpecs((v) => !v)}
                  className={showSpecs ? 'filter-chip-active' : 'filter-chip-inactive'}
                  aria-pressed={showSpecs}
                >
                  Specs
                </button>
                <button
                  type="button"
                  onClick={() => setTopLevelOnly((v) => !v)}
                  className={topLevelOnly ? 'filter-chip-active' : 'filter-chip-inactive'}
                  aria-pressed={topLevelOnly}
                >
                  Top-level only
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setCategory('All');
                      setBrand('All');
                      setTopLevelOnly(false);
                      setShowSpecs(true);
                      setSort(canEdit ? 'order' : 'category');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-300 min-h-[44px] px-2 cursor-pointer"
                  >
                    Clear all
                  </button>
                )}
              </div>}
              <div className={`grid grid-cols-1 ${exploreView ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-3`}>
                <div>
                  <label htmlFor="pub-cat" className="input-label">
                    Category
                  </label>
                  <select
                    id="pub-cat"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="select-surface w-full px-3"
                  >
                    <option value="All">All categories</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="pub-brand" className="input-label">
                    Brand
                  </label>
                  <select
                    id="pub-brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="select-surface w-full px-3"
                  >
                    <option value="All">All brands</option>
                    {brands.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
                {!exploreView && <div>
                  <label htmlFor="pub-sort" className="input-label">
                    Sort
                  </label>
                  <select
                    id="pub-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value as PublicBuildFilters['sort'])}
                    className="select-surface w-full px-3"
                  >
                    <option value="order">Build order</option>
                    <option value="category">Category</option>
                    <option value="name">Name</option>
                    <option value="brand">Brand</option>
                    <option value="newest">Newest</option>
                  </select>
                </div>}
              </div>
            </div>
          )}
        </div>

        {exploreView && isOwner && carShowAvailable ? (
          <div className="flex items-center pt-1">
            <button
              type="button"
              className="min-h-[40px] px-3 rounded-md bg-orange-500/15 text-orange-100 text-sm font-medium hover:bg-orange-500/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400"
              onClick={() => { if (isOwner && carShowAvailable) setCarShowMode(true); }}
            >
              Start Car Show Mode
            </button>
          </div>
        ) : null}
      </div>
      ) : null}

      {filtered.length === 0 && !activeCarShowMode ? (
        <p className="text-sm text-gray-500 py-6 text-center">No parts match these filters.</p>
      ) : (activeCarShowMode || view === 'explore') ? (
        <Suspense
          fallback={
            <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a]/40 py-12 text-center text-sm text-zinc-500">
              Loading explorer…
            </div>
          }
        >
          <BuildExplorer
            modifications={activeCarShowMode ? allDetailMods : exploreMods}
            allModifications={allDetailMods}
            showPricing={showPricing}
            showSpecs
            isOwner={canEdit && !activeCarShowMode}
            focusId={exploreFocus}
            searchQuery={activeCarShowMode ? '' : search}
            onFocusChange={setExploreFocus}
            onEdit={canEdit && !activeCarShowMode ? onEditModification : undefined}
            onAddChild={canEdit && !activeCarShowMode ? onAddChildModification : undefined}
            carShowMode={activeCarShowMode}
            carShowSnapshot={carShowContent ? { ...carShowContent, modifications: carShowContent.modifications.map(toDetailMod), allModifications: carShowContent.modifications.map(toDetailMod) } : undefined}
            carShowView={view === 'list' ? 'list' : 'explore'}
            onCarShowViewChange={setView}
            canStartCarShow={isOwner && carShowAvailable}
            onCarShowModeChange={(on) => { if (!on || (isOwner && carShowAvailable)) setCarShowMode(on); }}
            carShowFromQr={carShowFromQr}
            vehicleLabel={vehicleLabel}
            vehicleImageUrl={vehicleImageUrl}
            vehicleGallery={vehicleGallery}
            collectorCard={collectorCard}
            hideGuideToolbar
          />
        </Suspense>
      ) : view === 'timeline' ? (
        <>
          {canEdit && (
            <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a]/40 px-3.5 py-2.5 flex flex-wrap items-center gap-2 justify-between">
              <p className="hidden md:block text-sm text-muted">
                Timeline shows install history. Organize nesting in List.
              </p>
              <div className="flex flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                <button type="button" className="btn-ghost btn-sm flex-1 md:flex-none" onClick={() => goToListOrganize(false)}>
                  Open List
                </button>
                <button type="button" className="btn-secondary btn-sm flex-1 md:flex-none" onClick={() => goToListOrganize(true)}>
                  Select parts
                </button>
              </div>
            </div>
          )}
          <ModificationsTimelineView
            modifications={detailMods}
            isOwner={canEdit}
            showPricing={showPricing}
            onEdit={canEdit ? onEditModification : undefined}
            onDelete={canEdit ? onDeleteModification : undefined}
            onMove={
              canEdit && onOrganizeMove
                ? (mod) => {
                    const full = allDetailMods.find((m) => String(m.id) === String(mod.id));
                    if (full) setMoveTargets([full]);
                  }
                : undefined
            }
          />
        </>
      ) : view === 'card' ? (
        <>
          {canEdit && (
            <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a]/40 px-3.5 py-2.5 flex flex-wrap items-center gap-2 justify-between">
              <p className="hidden md:block text-sm text-muted">
                Browse and open nested parts here. Organize many parts in List.
              </p>
              <div className="flex flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                <button type="button" className="btn-ghost btn-sm flex-1 md:flex-none" onClick={() => goToListOrganize(false)}>
                  Open List
                </button>
                <button type="button" className="btn-secondary btn-sm flex-1 md:flex-none" onClick={() => goToListOrganize(true)}>
                  Select parts
                </button>
              </div>
            </div>
          )}
          <ModificationNestedCards
            modifications={detailMods}
            isOwner={canEdit}
            showPricing={showPricing}
            showSpecs={showSpecs}
            onEdit={canEdit ? onEditModification : undefined}
            onDelete={canEdit ? onDeleteModification : undefined}
            onMove={
              canEdit && onOrganizeMove
                ? (mod) => setMoveTargets([mod])
                : undefined
            }
          />
        </>
      ) : (
        <ModificationNestedList
          searchQuery={search}
          modifications={detailMods}
          allModifications={allDetailMods}
          isOwner={canEdit}
          showPricing={showPricing}
          onEdit={canEdit ? onEditModification : undefined}
          onDelete={canEdit ? onDeleteModification : undefined}
          onMove={
            canEdit && onOrganizeMove
              ? (mod) => setMoveTargets([mod])
              : undefined
          }
          onAddInside={canEdit ? onAddChildModification : undefined}
          onReorderSibling={
            canEdit && onOrganizeReorder
              ? async ({ parentId, orderedIds }) => {
                  await onOrganizeReorder({
                    parentModificationId: parentId ? parseInt(parentId, 10) : null,
                    orderedIds: orderedIds.map((id) => parseInt(id, 10)),
                  });
                }
              : undefined
          }
          onExport={
            canEdit
              ? () => {
                  setExportMods(null);
                  setExportOpen(true);
                }
              : undefined
          }
          selectMode={selectMode}
          onSelectModeChange={canEdit ? updateSelectMode : undefined}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          onBulkMove={
            canEdit && onOrganizeBulkMove
              ? () => setMoveTargets(selectedMods)
              : undefined
          }
          onBulkCategory={
            canEdit && onOrganizeBulkCategory
              ? () => {
                  setBulkCategory(selectedMods[0]?.category || categoryOptions[0]?.name || '');
                  setBulkIncludeNested(false);
                  setBulkCategoryOpen(true);
                }
              : undefined
          }
          onBulkDelete={
            canEdit && onOrganizeBulkDelete
              ? () => setBulkDeleteOpen(true)
              : undefined
          }
          onBulkExport={
            canEdit
              ? () => {
                  setExportMods(
                    modifications.filter((m) => selectedIds.has(String(m.id ?? '')))
                  );
                  setExportOpen(true);
                }
              : undefined
          }
        />
      )}

      {canEdit && (
        <>
          <ExportBuildListModal
            isOpen={exportOpen}
            onClose={() => {
              setExportOpen(false);
              setExportMods(null);
            }}
            modifications={exportMods || modifications}
            vehicleLabel={vehicleLabel}
            showPricingOption={showPricing}
          />

          {moveTargets && (
            <MoveModificationModal
              isOpen={!!moveTargets}
              onClose={() => setMoveTargets(null)}
              targets={moveTargets}
              allModifications={allDetailMods}
              mode={moveTargets.length > 1 ? 'bulk' : 'single'}
              onConfirm={async ({
                parent_modification_id,
                category: cat,
                before_modification_id,
                after_modification_id,
              }) => {
                if (moveTargets.length === 1 && onOrganizeMove) {
                  await onOrganizeMove({
                    modificationId: parseInt(moveTargets[0].id, 10),
                    parentModificationId: parent_modification_id,
                    category: cat,
                    beforeModificationId: before_modification_id,
                    afterModificationId: after_modification_id,
                  });
                } else if (onOrganizeBulkMove) {
                  await onOrganizeBulkMove({
                    modificationIds: moveTargets.map((m) => parseInt(m.id, 10)),
                    parentModificationId: parent_modification_id,
                    category: cat,
                    beforeModificationId: before_modification_id,
                    afterModificationId: after_modification_id,
                  });
                }
                updateSelectMode(false);
              }}
            />
          )}

          {bulkCategoryOpen && (
            <div
              className="modal-overlay z-[70]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-cat-title"
            >
              <div className="modal-panel modal-panel-md space-y-4">
                <h2 id="bulk-cat-title" className="text-lg font-semibold text-white">
                  Change category for {selectedMods.length} parts
                </h2>
                <p className="text-xs text-gray-500">
                  By default only the selected parts change. Nested parts keep their categories unless you
                  include them.
                </p>
                <select
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                  className={inputClass}
                >
                  {categoryOptions.map((c: { name: string }) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                  <input
                    type="checkbox"
                    checked={bulkIncludeNested}
                    onChange={(e) => setBulkIncludeNested(e.target.checked)}
                    className="checkbox-control"
                  />
                  Include nested parts
                </label>
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    className="min-h-[44px] px-4 text-gray-400"
                    onClick={() => setBulkCategoryOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-4 rounded-lg bg-orange-600 text-white font-semibold"
                    onClick={async () => {
                      if (!onOrganizeBulkCategory || !bulkCategory) return;
                      await onOrganizeBulkCategory({
                        modificationIds: selectedMods.map((m) => parseInt(m.id, 10)),
                        category: bulkCategory,
                        includeNested: bulkIncludeNested,
                      });
                      setBulkCategoryOpen(false);
                      updateSelectMode(false);
                    }}
                  >
                    Change category
                  </button>
                </div>
              </div>
            </div>
          )}

          {bulkDeleteOpen && (
            <div
              className="modal-overlay z-[70]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bulk-del-title"
            >
              <div className="modal-panel modal-panel-md space-y-4">
                <h2 id="bulk-del-title" className="text-lg font-semibold text-white">
                  Delete {selectedMods.length} selected parts?
                </h2>
                {nestedImpact.parentsWithKids > 0 ? (
                  <p className="text-sm text-gray-300">
                    {nestedImpact.parentsWithKids} of these parts contain {nestedImpact.nested} nested
                    parts.
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">This cannot be undone from this screen.</p>
                )}
                <p className="text-xs text-gray-500">
                  Choose what happens to nested parts. There is no undo after delete.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="min-h-[44px] px-4 rounded-lg border border-white/[0.10] text-gray-200 text-left text-sm"
                    onClick={async () => {
                      if (!onOrganizeBulkDelete) return;
                      await onOrganizeBulkDelete({
                        modificationIds: selectedMods.map((m) => parseInt(m.id, 10)),
                        childPolicy: 'promote',
                      });
                      setBulkDeleteOpen(false);
                      updateSelectMode(false);
                    }}
                  >
                    Keep nested parts and move them up
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-4 rounded-lg border border-red-800/50 text-red-300 text-left text-sm"
                    onClick={async () => {
                      if (!onOrganizeBulkDelete) return;
                      await onOrganizeBulkDelete({
                        modificationIds: selectedMods.map((m) => parseInt(m.id, 10)),
                        childPolicy: 'cascade',
                      });
                      setBulkDeleteOpen(false);
                      updateSelectMode(false);
                    }}
                  >
                    Delete everything inside
                  </button>
                  <button
                    type="button"
                    className="min-h-[44px] px-4 text-gray-400 text-sm"
                    onClick={() => setBulkDeleteOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VehicleBuildViews;
